import type { Lang } from '@aera/schema';
import { PRIVACY_CHIP } from '@aera/content/client';
import { AiOrb } from '@aera/ui';
import { ChevronLeft, Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { SaveStatus } from '../lib/autosave.js';
import { Button, LangToggle } from '../components/ui.js';
import { displayedProgress } from './flow.js';

/**
 * Question screen anatomy: a thin gradient bar at the very top (fast through the quick chip screens,
 * slower later), the module name and a short textual cue, the privacy chip, and the language toggle.
 * The module name keeps at least 8rem: where the chip and toggle do not fit beside it (English on a
 * 640 px column, small phones) they wrap to a second line rather than squeeze it to a few letters.
 */
export function TopBar({
  progress,
  moduleLabel,
  cue,
  orbFill,
  lang,
  onLang,
  onPrivacy,
  announce,
}: {
  progress: number;
  moduleLabel?: string;
  cue?: string;
  orbFill?: number;
  lang: Lang;
  onLang: (lang: Lang) => void;
  onPrivacy: () => void;
  announce?: string;
}) {
  const { t } = useTranslation();
  const shown = displayedProgress(progress);
  return (
    <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-md lp-noprint">
      <div
        className="h-[3px] w-full bg-hair"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress * 100)}
        aria-label={t('question.progressLabel')}
      >
        <div className="lp-gradient-bar h-full origin-left transition-transform duration-700 ease-[cubic-bezier(.16,1,.3,1)]" style={{ transform: `scaleX(${shown})` }} />
      </div>
      <div className="mx-auto flex max-w-[640px] flex-wrap items-center gap-x-2.5 gap-y-2 px-4 py-3">
        {orbFill !== undefined && <AiOrb fill={orbFill} still className="h-7 w-7 shrink-0" />}
        {/* Only a screen with a module name or cue keeps the 8rem column: an empty one would still push
            the chip and toggle onto a second line (consent, follow-ups, member check). */}
        {(moduleLabel || cue) && (
          <div className="min-w-[8rem] flex-1 leading-tight">
            {moduleLabel && <div className="line-clamp-3 text-[14px] font-medium">{moduleLabel}</div>}
            {cue && <div className="truncate text-[12.5px] text-ink-2">{cue}</div>}
          </div>
        )}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <PrivacyChip lang={lang} onClick={onPrivacy} />
          <LangSwitch lang={lang} onLang={onLang} />
          {/* A wrapper carries the breakpoint: LangToggle's own inline-flex would override a hidden class. */}
          <div className="hidden sm:block">
            <LangToggle lang={lang} onChange={onLang} />
          </div>
        </div>
      </div>
      <div aria-live="polite" className="sr-only">
        {announce}
      </div>
    </header>
  );
}

/** Below sm the language toggle is one small button that switches to the other language. */
function LangSwitch({ lang, onLang }: { lang: Lang; onLang: (lang: Lang) => void }) {
  const { t } = useTranslation();
  const other: Lang = lang === 'zh' ? 'en' : 'zh';
  return (
    // The name is in the session language; only the visible word is in the other one (a lang on the
    // button would cover its aria-label too, and a screen reader would read it in the wrong voice).
    <button
      type="button"
      onClick={() => onLang(other)}
      aria-label={t('question.langSwitch', { lang: t(`common.${other}`) })}
      className="inline-flex min-h-[32px] items-center rounded-full border border-hair px-2.5 text-[12.5px] text-ink-2 transition-colors hover:text-black sm:hidden"
    >
      <span lang={other}>{other === 'en' ? 'EN' : '中文'}</span>
    </button>
  );
}

/**
 * The plan's chip text from sm up; on a phone a short form keeps words beside the lock. The name comes
 * from the words shown (no aria-label), so it starts with what a voice-control user sees and says:
 * on a phone the short words, then the full promise for a screen reader; from sm up the full promise.
 */
export function PrivacyChip({ lang, onClick }: { lang: Lang; onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-hair px-2.5 py-1 text-[12px] text-ink-2 transition-colors hover:text-black"
    >
      <Lock className="h-3.5 w-3.5 text-indigo" aria-hidden />
      <span className="sm:hidden">{t('privacy.chipShort')}</span>
      {/* Starts with the colon, not a space: a space at the edge of an element is dropped from a name. */}
      <span className="sr-only sm:hidden">{`${lang === 'zh' ? '：' : ': '}${PRIVACY_CHIP[lang]}`}</span>
      <span className="hidden sm:inline">{PRIVACY_CHIP[lang]}</span>
    </button>
  );
}

export function Footer({
  onBack,
  onNext,
  nextLabel,
  nextDisabled,
  busy,
  saveStatus,
  children,
}: {
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  busy?: boolean;
  saveStatus?: SaveStatus;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <footer className="safe-bottom sticky bottom-0 z-20 border-t border-hair bg-white/95 backdrop-blur-md lp-noprint">
      {children && <div className="mx-auto max-w-[640px] px-4 pt-3">{children}</div>}
      <div className="mx-auto flex max-w-[640px] items-center gap-3 px-4 pt-3">
        {onBack ? (
          <Button variant="ghost" onClick={onBack} className="-ml-2 px-3">
            <ChevronLeft className="h-4 w-4" aria-hidden />
            {t('common.back')}
          </Button>
        ) : (
          <span />
        )}
        {/* The only sign that answers are not reaching the server: readable red, not decorative --ink-3. */}
        <span className="flex-1 text-center text-[12px] text-red-700" aria-live="polite">
          {saveStatus === 'error' ? t('common.saveFailed') : ''}
        </span>
        {onNext && (
          <Button onClick={onNext} disabled={nextDisabled} busy={busy} className="min-w-[120px]">
            {nextLabel ?? t('common.continue')}
          </Button>
        )}
      </div>
    </footer>
  );
}

/** One column, max 640 px, built for a 6-inch phone first. */
export function Page({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <main className={`mx-auto w-full max-w-[640px] flex-1 px-4 pb-8 pt-6 ${className}`}>{children}</main>;
}
